import { PlusIcon } from "@radix-ui/react-icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { createContext, use, useState } from "react";

import { ErrorBanner } from "@/components/Callout";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SimpleSelect } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { P } from "@/components/ui/typography";
import {
  createLinkTrackedPost,
  createProgrammaticAsset,
  getCurrentUserQueryOptions,
  hydratePost,
} from "@/lib/api";
import { Post } from "@/lib/types";
import { atUriToBskyUrl } from "@/lib/utils";

const DEFAULT_DID = "did:plc:z2gi4cefpif43ak7fcl45cbi";

const acceptedHashtags = [
  "ad",
  "sponsored",
  "advertisement",
  "ads",
  "promoted",
  "promotion",
  "sponcon",
  "paid",
  "partner",
];

export const IsProgrammaticContext = createContext<boolean>(false);

function AddStickyPostDialogContent({
  onAddStickyPost,
  onClose,
  isPending,
}: {
  onAddStickyPost: (options: AddStickPostCreateOptions) => Promise<void> | void;
  onClose: () => void;
  isPending?: boolean;
}) {
  const [did, setDid] = useState<string>("default");
  const isProgrammatic = use(IsProgrammaticContext);
  const { data: user } = useQuery(getCurrentUserQueryOptions());
  const [type, setType] = useState<"create" | "link">("link");
  const [postText, setPostText] = useState<string>("");
  const mutation = useMutation({
    onSuccess: onClose,
    onError: (error: AxiosError) => console.error(error),
    mutationFn: async (data: FormData) => {
      if (type === "link") {
        const post_url = data.get("post_url") as string;
        const post = await hydratePost(post_url);

        return onAddStickyPost({ post_url, post });
      } else if (isProgrammatic) {
        const link = data.get("embed") as string | undefined;
        const createRes = await createProgrammaticAsset({
          text: postText,
          link,
          did: did === "default" ? DEFAULT_DID : did,
        });
        await new Promise((resolve) => setTimeout(resolve, 1000));
        const post = await hydratePost(createRes.uri);

        return onAddStickyPost({
          post_url: atUriToBskyUrl(createRes.uri),
          post,
        });
      } else if (type === "create") {
        const link = data.get("embed") as string | undefined;
        const createRes = await createLinkTrackedPost({
          text: postText,
          link,
          include_tracking: Boolean(link),
        });
        const post = await hydratePost(createRes.data.uri);

        return onAddStickyPost({
          post_url: atUriToBskyUrl(createRes.data.uri),
          post,
        });
      }
    },
  });

  const createDisabled =
    type === "create" &&
    !acceptedHashtags.some((hashtag) =>
      postText.toLowerCase().includes(hashtag),
    );

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const form = new FormData(e.target as HTMLFormElement);

        mutation.mutate(form);
      }}
    >
      <DialogHeader>
        <DialogTitle>Add Post</DialogTitle>
        <DialogDescription className="flex flex-col gap-2">
          <p>Enter a URL or create a post to be a part of your campaign.</p>
          {isPending && (
            <p>
              Feed operators will be notified and will have the option to accept
              or reject the post.
            </p>
          )}
          <p>
            The post must contain one of the following hashtags to comply with
            local laws:
          </p>
          <ul className="flex flex-wrap gap-2 mt-2">
            {acceptedHashtags.map((hashtag) => (
              <li
                key={hashtag}
                className="px-2 py-0.5 text-sm bg-slate-subtle border rounded border-slate-6 dark:border-slatedark-6"
              >
                #{hashtag}
              </li>
            ))}
          </ul>
        </DialogDescription>
      </DialogHeader>
      <Separator />

      {mutation.error && <ErrorBanner error={mutation.error} />}

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          <Label htmlFor="type">Type</Label>
          <SimpleSelect
            options={[
              { label: "Link", value: "link" },
              { label: "Create", value: "create" },
            ]}
            aria-label="Type of post"
            name="type"
            value={type}
            onValueChange={(value) => setType(value as "create" | "link")}
          />
        </div>

        {type === "link" ? (
          <div className="flex flex-col gap-3">
            <Label htmlFor="post-url">Post URL</Label>
            <Input id="post-url" type="text" name="post_url" required />
          </div>
        ) : (
          <>
            {isProgrammatic && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="asset-did">DID (optional)</Label>
                <SimpleSelect
                  options={[
                    {
                      label: `Graze Sponsored (${DEFAULT_DID})`,
                      value: "default",
                    },
                    ...(user?.user_id
                      ? [
                          {
                            label: `You (${user.user_id})`,
                            value: user.user_id,
                          },
                        ]
                      : []),
                  ]}
                  aria-label="DID"
                  name="did"
                  value={did}
                  onValueChange={(value) => setDid(value)}
                  placeholder="Choose a DID"
                  id="asset-did"
                />
              </div>
            )}
            <div className="flex flex-col gap-3">
              <Label htmlFor="post">Post</Label>
              <Textarea
                id="post"
                name="post"
                required
                value={postText}
                onValueChange={setPostText}
              />
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <Label htmlFor="embed">Link Embed</Label>
              </div>
              <P className="text-xs text-slate-dim">
                Optionally include a link that will be displayed in the post. We
                will track clicks on this link.
              </P>
              <Input id="embed" type="text" name="embed" />
            </div>
          </>
        )}
      </div>

      <DialogFooter>
        {type === "create" ? (
          <SimpleTooltip
            message="Post missing required hashtags."
            disabled={!createDisabled}
          >
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
            <div tabIndex={createDisabled ? 0 : -1}>
              <Button
                type="submit"
                isLoading={mutation.isPending}
                disabled={createDisabled}
              >
                Add
              </Button>
            </div>
          </SimpleTooltip>
        ) : (
          <Button type="submit" isLoading={mutation.isPending}>
            Add
          </Button>
        )}
      </DialogFooter>
    </form>
  );
}

export interface AddStickPostCreateOptions {
  post_url: string;
  post: Post;
  expiration?: Date;
  order?: number;
}

export function AddStickyPostToCampaignButton({
  onAddStickyPost,
  isPending,
}: {
  onAddStickyPost: (options: AddStickPostCreateOptions) => Promise<void> | void;
  isPending?: boolean;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const message = "Add a post to be a part of your campaign";

  return (
    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
      <SimpleTooltip message={message}>
        <DialogTrigger asChild>
          <Button
            size="smIcon"
            variant="ghost"
            className="flex items-center justify-center gap-2 rounded-lg p-4 text-slate-dim"
          >
            <span className="sr-only">{message}</span>
            <PlusIcon className="h-4 w-4" />
          </Button>
        </DialogTrigger>
      </SimpleTooltip>
      <DialogContent>
        <AddStickyPostDialogContent
          isPending={isPending}
          onAddStickyPost={onAddStickyPost}
          onClose={() => setDialogOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
